import { useSize } from '@hover/blueprint';
import { useMotionValueEvent, useScroll } from 'framer-motion';
import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

type UseScrollableLeftAndRightArgs = {
  ref: RefObject<HTMLDivElement>;
  scrollAmount?: number;
  contentLoading?: boolean;
};

export const useScrollableLeftAndRight = ({
  ref,
  scrollAmount,
  contentLoading,
}: UseScrollableLeftAndRightArgs) => {
  const { scrollX, scrollXProgress } = useScroll({ container: ref });
  const [showLeftArrow, setShowLeftArrow] = useState(
    scrollXProgress.get() > 0 && scrollX.get() > 0,
  );
  const [showRightArrow, setShowRightArrow] = useState(
    scrollXProgress.get() !== 1,
  );
  const size = useSize(ref);
  const width = size?.width ?? 0;

  const checkScrollability = useCallback(() => {
    setShowLeftArrow(scrollXProgress.get() > 0 && scrollX.get() > 0);
    setShowRightArrow(scrollXProgress.get() < 1);
  }, [scrollX, scrollXProgress]);

  useMotionValueEvent(scrollX, 'change', x => {
    const progress = Math.round(scrollXProgress.get() * 100);

    setShowLeftArrow(progress > 0 && x > 0);
    setShowRightArrow(progress !== 100);
  });

  useEffect(() => {
    checkScrollability();
  }, [scrollX, scrollXProgress, contentLoading, checkScrollability, width]);

  const scroll = useCallback(
    (direction: 'next' | 'previous') => {
      const x = scrollAmount ?? width;

      const amount =
        direction === 'next' ? scrollX.get() + x : scrollX.get() - x;

      ref.current?.scroll({ left: amount, behavior: 'smooth' });
    },
    [ref, scrollAmount, scrollX, width],
  );

  return {
    canScrollLeft: contentLoading ? false : showLeftArrow,
    canScrollRight: contentLoading ? false : showRightArrow,
    scroll,
  };
};
